import { faPlay } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Grid, Skeleton, Stack, Typography } from '@mui/material';
import { AppGlobalClassKeys } from 'app/appGlobalStyles';
import { AppImageDimension } from 'app/appImages';
import { MprcClickable } from 'components';
import { MprcMediaViewer } from 'components/MprcMediaViewer';
import { ClassroomViewDto, TrainingViewDto } from 'generated/CoreApi_v1';
import React, { useState } from 'react';
import { LmsTrainingImage } from 'views/lms/components/LmsTypeImage';
import { TrainingBookmarkButton } from 'views/lms/components/TrainingBookmarkButton';
import { TrainingLikeButton } from 'views/lms/components/TrainingLikeButton';
import { TrainingShareButton } from 'views/lms/components/TrainingShareButton';
import { ITraininRegistrationProps } from 'views/lms/student/Registration';

///////////////////////////////////////////////////////////////////////////////

const ImageOrVideoThumbnail = ({ training }: ITraininRegistrationProps) => {
    const [open, setOpen] = useState<boolean>(false);

    if (training == null)
        return null;

    if (training.promotionVideo != null) {
        return (
            <div style={{ position: "relative" }}>
                <MprcClickable
                    onClick={() => setOpen(true)}
                >
                    <MprcMediaViewer
                        open={open}
                        file={training.promotionVideo!}
                        onClose={() => setOpen(false)}
                    />

                    <LmsTrainingImage
                        thumbnailUrl={training.promotionVideoThumbnailUrl ?? training.thumbnailUrl}
                        width={AppImageDimension.ThumbnailWide.width}
                        height={AppImageDimension.ThumbnailWide.height}
                        contentType={training.contentType}
                    />

                    <div className={AppGlobalClassKeys.centerChild}>
                        <Box sx={{ p: 1, pr: 2, pl: 2, borderRadius: 1, backgroundColor: "rgba(0, 0, 0, 0.5)" }}>
                            <FontAwesomeIcon icon={faPlay} size="2x" color="white" />
                        </Box>
                    </div>
                </MprcClickable>
            </div>
        );
    }

    return (
        <LmsTrainingImage
            thumbnailUrl={training.thumbnailUrl}
            width={AppImageDimension.ThumbnailWide.width}
            height={AppImageDimension.ThumbnailWide.height}
            contentType={training.contentType}
        />
    );
};

interface IProps extends ITraininRegistrationProps {
    classrooms: ClassroomViewDto[];
    selectedClassroom: number;
    showClassroomName: boolean;
    interactionLocation: "none" | "bottom" | "aside";
    actionButton?: React.ReactNode;
    onChange: (training: Partial<TrainingViewDto>) => void;
}

export const RegistrationViewHeader = ({ training, classrooms, selectedClassroom, showClassroomName, interactionLocation = "aside", actionButton, onChange }: IProps) => {
    return training == null
        ? (
            <Grid container justifyContent="space-between" alignItems="start" height={AppImageDimension.ThumbnailWide.height} spacing={1}>
                <Grid>
                    <Skeleton variant="rectangular" width={AppImageDimension.ThumbnailWide.width} height={AppImageDimension.ThumbnailWide.height} />
                </Grid>
                <Grid size="grow" height="100%">

                    <Grid container direction="column" height="100%" justifyContent="space-between">
                        <Grid>
                            <Grid>
                                <Skeleton variant="rectangular" width="100%" height={44} />
                            </Grid>
                            <Grid>
                                <Skeleton variant="rectangular" width="100%" height={22} />
                            </Grid>
                        </Grid>
                        <Grid>
                            <Grid container justifyContent="space-between" alignItems="center" spacing={1}>
                                <Grid>
                                    <Skeleton variant="rectangular" width={40} height={22} />
                                </Grid>
                                <Grid>
                                    <Stack direction="row" spacing={1}>
                                        <Skeleton variant="rectangular" width={40} height={22} />
                                        <Skeleton variant="rectangular" width={40} height={22} />
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        )
        : (
            <div>
                <Grid container justifyContent="space-between" alignItems="start" spacing={1} height={AppImageDimension.ThumbnailWide.height + 10}>
                    <Grid>
                        <ImageOrVideoThumbnail training={training!} />
                    </Grid>
                    <Grid size="grow" height="100%">

                        <Grid container direction="column" height="100%" justifyContent="space-between" wrap="nowrap">
                            <Grid>
                                <Grid>
                                    <Typography variant="h2" textTransform="unset" color="black"
                                        sx={{
                                            width: "100%",
                                            display: "-webkit-box",
                                            "-webkit-line-clamp": "4",
                                            "-webkit-box-orient": "vertical",
                                            "overflow": "hidden"
                                        }}
                                    >
                                        {training.name}
                                    </Typography>
                                </Grid>
                                <Grid>
                                    <Typography variant="captionHeader" component="div">
                                        {training.contentTypeName}
                                    </Typography>
                                    {
                                        showClassroomName && classrooms.length == 1 &&
                                        <Typography variant="captionHeader" component="div">
                                            {classrooms[selectedClassroom].name}
                                        </Typography>
                                    }
                                </Grid>
                            </Grid>
                            {
                                interactionLocation == "aside" &&
                                <Grid>
                                    <Grid container justifyContent="space-between" alignItems="center" spacing={1}>
                                        {
                                            actionButton &&
                                            <Grid>
                                                {actionButton}
                                            </Grid>
                                        }
                                        <Grid>
                                            {
                                                training.enableLike &&
                                                <TrainingLikeButton
                                                    trainingId={training.trainingId}
                                                    readonly={false}
                                                    liked={training.liked}
                                                    count={training.likeCount}
                                                    onChange={(liked, likeCount) => onChange({ liked, likeCount })}
                                                />
                                            }
                                        </Grid>
                                        <Grid>
                                            <Stack direction="row" spacing={1}>
                                                <TrainingBookmarkButton
                                                    trainingId={training.trainingId}
                                                    bookmarked={training.bookmarked}
                                                    onChange={bookmarked => onChange({ bookmarked })}
                                                />
                                                <TrainingShareButton
                                                    trainingId={training.trainingId}
                                                    trainingName={training.name}
                                                    shareOption={training.shareOption}
                                                    trainingHasAudienceDefined={training.hasAudienceDefined}
                                                    enableShareInChannelAndLists={training.enableShareInChannelAndLists}
                                                />
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            }
                        </Grid>


                    </Grid>
                </Grid>

                {
                    interactionLocation == "bottom" &&
                    <Grid container justifyContent="space-between" alignItems="center">
                        {
                            actionButton &&
                            <Grid width={AppImageDimension.ThumbnailWide.width}>
                                {actionButton}
                            </Grid>
                        }
                        <Grid size="grow">
                            <Grid container justifyContent="space-between" alignItems="center">
                                <Grid>
                                    {
                                        training.enableLike &&
                                        <TrainingLikeButton
                                            trainingId={training.trainingId}
                                            readonly
                                            liked={training.liked}
                                            count={training.likeCount}
                                            onChange={(liked, likeCount) => onChange({ liked, likeCount })}
                                        />
                                    }
                                </Grid>
                                <Grid>
                                    <Stack direction="row" spacing={1}>
                                        <TrainingBookmarkButton
                                            trainingId={training.trainingId}
                                            bookmarked={training.bookmarked}
                                            onChange={bookmarked => onChange({ bookmarked })}
                                        />
                                        <TrainingShareButton
                                            trainingId={training.trainingId}
                                            trainingName={training.name}
                                            shareOption={training.shareOption}
                                            trainingHasAudienceDefined={training.hasAudienceDefined}
                                            enableShareInChannelAndLists={training.enableShareInChannelAndLists}
                                        />
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                }
            </div>
        );
};
